import { Component, inject, OnDestroy, OnInit, Signal } from '@angular/core';

import { Store } from '@ngrx/store';
import { DeviceDetectorService } from 'ngx-device-detector';
import { combineLatest, interval, merge, Observable, of, Subject } from 'rxjs';
import { catchError, mergeMap, startWith, switchMap, takeUntil } from 'rxjs/operators';

import { AudioVideoConnectionMonitorService } from 'src/app/features/audio-video-connection-monitor/services/audio-video-connection-monitor.service';
import { NavigatorMediaDeviceWrapperService } from 'src/app/features/audio-video-connection-monitor/services/navigator-media-device-wrapper.service';

import { LobbySelectors } from 'src/app/features/lobby';
import { ModalsActions } from 'src/app/features/modals';
import { PackageUser } from 'src/app/features/package-users';
import { PackagesSelectors } from 'src/app/features/packages';
import { RootStoreState } from 'src/app/store';

@Component({
    selector: 'app-audio-video-connection-monitor-v2',
    template: '',
    standalone: false
})
export class AudioVideoConnectionMonitorV2Component implements OnInit, OnDestroy {
  private onDeviceChange$: Observable<Event>;

  private readonly audioVideoConnectionMonitorService = inject(AudioVideoConnectionMonitorService);
  private readonly deviceDetectorService = inject(DeviceDetectorService);
  private readonly navigatorMediaDeviceWrapperService = inject(NavigatorMediaDeviceWrapperService);
  private readonly store: Store<RootStoreState.State> = inject(Store);

  private readonly destroySubject: Subject<boolean> = new Subject<boolean>();

  private readonly getActivePackageUser: Signal<PackageUser> = this.store.selectSignal(
    LobbySelectors.getActivePackageUser
  );

  static identifier = 'AudioVideoConnectionMonitorV2Component';
  static pollingIntervalInMs = 10000;

  ngOnDestroy(): void {
    this.destroySubject.next(undefined);
    this.destroySubject.complete();
  }

  ngOnInit(): void {
    if (!this.isDeviceSupported()) {
      return;
    }

    this.store
      .select(PackagesSelectors.packageCompleteOrCancelled)
      .pipe(takeUntil(this.destroySubject))
      .subscribe((packageCompleteOrCancelled) => {
        if (packageCompleteOrCancelled) {
          this.store.dispatch(ModalsActions.ClearModalComponent());
        }
      });

    // When the onDeviceChange event emits, enumerate all the most recent connected devices
    // Not all browsers support the ondevicechange event.  If a browser doesn't support ondevicechange,
    // fallback to long-polling
    if (this.navigatorMediaDeviceWrapperService.browserSupportsDeviceChange()) {
      this.onDeviceChange$ = this.navigatorMediaDeviceWrapperService
        .onDeviceChange()
        .pipe(takeUntil(this.destroySubject));
    } else {
      this.onDeviceChange$ = interval(
        AudioVideoConnectionMonitorV2Component.pollingIntervalInMs
      ).pipe(
        switchMap((e) => of(new Event('devicechange'))),
        takeUntil(this.destroySubject)
      );
    }

    // This prompts the user for permissions to access their camera and mic
    const mediaStream$ = this.navigatorMediaDeviceWrapperService
      .getUserMedia({ audio: true, video: true })
      .pipe(
        // This obtains the MediaStream from their camera and mic after the user grants permissions
        switchMap(() =>
          this.navigatorMediaDeviceWrapperService.getUserMedia({
            audio: {
              deviceId: this.audioVideoConnectionMonitorService.selectedAudioDevice().deviceId,
            },
            video: {
              deviceId: this.audioVideoConnectionMonitorService.selectedVideoDevice().deviceId,
            },
          })
        ),
        mergeMap((mediaStream) => {
          // This will listen for permission changes on the camera and mic by subscribing to the Track ended events
          const audioEnded =
            this.navigatorMediaDeviceWrapperService.getOnMediaStreamTrackEnded<Event>(
              mediaStream.getAudioTracks()[0]
            );
          const videoEnded =
            this.navigatorMediaDeviceWrapperService.getOnMediaStreamTrackEnded<Event>(
              mediaStream.getVideoTracks()[0]
            );
          // Start off the observable stream by returning the current media stream, and emit the media stream everytime the track end event emits
          return merge(audioEnded, videoEnded).pipe(
            switchMap((e) => of(mediaStream)),
            startWith(mediaStream),
            takeUntil(this.destroySubject)
          );
        }),
        // Any time there is an error, emit an empty media stream
        // If the browser doesn't have permissions,when enumerateDevices() runs,it will return an emptydevice list
        catchError((err) => of(new MediaStream()))
      );

    // combine the selected devices w/ the device list to check if the selected
    // devices are no longer in the device list
    combineLatest([this.onDeviceChange$.pipe(startWith(new Event('Initialize'))), mediaStream$])
      .pipe(
        switchMap(() => this.navigatorMediaDeviceWrapperService.enumerateDevices()),
        takeUntil(this.destroySubject)
      )
      .subscribe((devices) => {
        if (this.audioVideoConnectionMonitorService.shouldDeviceChangeDispatchModal(devices)) {
          this.audioVideoConnectionMonitorService.dispatchModal();
        }
      });
  }

  /*
  This disables the AV connection monitor due to a bug where the AV connection monitor
  will break on refresh when re-connecting.
  This occurs on any device running iOS/iPadOS (macOS works fine).
  Some iPads come up as iOS, some devices come up as tablets with the "Mac" operating system.
  Also fixes the bug on Android devices where other opened tabs could not share the same device permissions.
  */
  isDeviceSupported() {
    const deviceInfo = this.deviceDetectorService.getDeviceInfo();

    // If the device monitor returns null for any reason, prevent Audio Video connection monitor from running
    // Or any iOS devices that might not register as a phone or tablet.
    if (!deviceInfo || deviceInfo.os === 'iOS') {
      return false;
    }

    const isMobile = this.deviceDetectorService.isMobile() || this.deviceDetectorService.isTablet();
    return !isMobile;
  }
}
